import { Controller } from "stimulus";
import confetti from "canvas-confetti";

// Carnation and Plum are listed twice so they come up twice as often.
const COLORS = ["#F65F5A", "#6C5BE7", "#29C884", "#8880FF", "#B8B3FF", "#FF8D80", "#F65F5A", "#6C5BE7"];
const SUCCESS_COLOR = "#29C884";
const SHAPES = ["square", "square", "circle"];
const SHOCKWAVES = [["#F65F5A", 9, 900], ["#6C5BE7", 14, 1150], ["#29C884", 20, 1400]];
// How long the next page's render is held so the celebration can play out.
const HOLD = 2700;

let stage, fire;

// One fixed layer outside <body> for the confetti and shockwaves: Turbo's render leaves it alone, so the
// confetti keeps falling onto the next page, and no clipping or scrolling ancestor (a modal) cuts it off.
function getStage() {
  if (!stage) {
    stage = document.createElement("div");
    stage.setAttribute("aria-hidden", "true");
    Object.assign(stage.style, { position: "fixed", inset: "0", overflow: "hidden", pointerEvents: "none", zIndex: "1000" });
    const canvas = document.createElement("canvas");
    Object.assign(canvas.style, { width: "100%", height: "100%" });
    stage.append(canvas);
    fire = confetti.create(canvas, { resize: true, disableForReducedMotion: true });
  }
  if (!stage.isConnected) document.documentElement.append(stage);
  return stage;
}

function burst(options) {
  getStage();
  fire({ colors: COLORS, shapes: SHAPES, decay: 0.94, gravity: 1.1, ticks: 320, ...options });
}

function sample(list) {
  return list[Math.floor(Math.random() * list.length)];
}

function isVisible(element) {
  return element.getClientRects().length > 0;
}

// Fades the transition blocks up one after another once the next page has rendered, then lets confetti rain.
function revealOnRender(selector, exits, reducedMotion) {
  document.addEventListener("turbo:render", () => {
    exits.forEach((animation) => animation.cancel());

    const blocks = [...document.querySelectorAll(selector)].filter(isVisible);
    blocks.forEach((block, i) => {
      if (reducedMotion) {
        block.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 150 });
      } else {
        block.animate(
          [{ opacity: 0, transform: "translateY(22px)" }, { opacity: 1, transform: "none" }],
          { duration: 650, delay: 120 + i * 70, easing: "cubic-bezier(.2,.8,.2,1)", fill: "backwards" }
        );
      }
    });
    if (reducedMotion) return;

    for (let wave = 0; wave < 4; wave++) {
      setTimeout(() => {
        for (let i = 0; i < 22; i++) {
          // canvas-confetti hands out colors and shapes in turn, so a single particle needs its own picked.
          burst({
            particleCount: 1, angle: 270, spread: 40, startVelocity: 3, origin: { x: Math.random(), y: -0.05 },
            colors: [sample(COLORS)], shapes: [sample(SHAPES)]
          });
        }
      }, wave * 180);
    }
  }, { once: true });
}

export default class extends Controller {
  static targets = ["mover", "presser", "rings", "button", "sheen", "label", "arrow", "check"];
  static values = { transition: String };

  connect() {
    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    this.phase = "idle";
    this.timers = [];

    this.form = this.buttonTarget.form;
    this.launch = this.launch.bind(this);
    this.responded = this.responded.bind(this);
    this.failed = this.failed.bind(this);
    this.holdRender = this.holdRender.bind(this);
    this.press = this.press.bind(this);
    this.release = this.release.bind(this);

    // A submit button celebrates on its form's submit rather than its click, so implicit submission counts
    // and a click the browser's validation stops doesn't.
    if (this.form) {
      this.form.addEventListener("submit", this.launch);
      this.form.addEventListener("turbo:before-fetch-response", this.responded);
      this.form.addEventListener("turbo:fetch-request-error", this.failed);
    } else {
      this.buttonTarget.addEventListener("click", this.launch);
    }
    this.buttonTarget.addEventListener("mousedown", this.press);
    this.buttonTarget.addEventListener("mouseup", this.release);

    this.startIdle();
  }

  disconnect() {
    this.form?.removeEventListener("submit", this.launch);
    this.form?.removeEventListener("turbo:before-fetch-response", this.responded);
    this.form?.removeEventListener("turbo:fetch-request-error", this.failed);
    this.buttonTarget.removeEventListener("click", this.launch);
    this.buttonTarget.removeEventListener("mousedown", this.press);
    this.buttonTarget.removeEventListener("mouseup", this.release);
    document.removeEventListener("turbo:before-render", this.holdRender);
    this.timers.forEach(clearTimeout);
  }

  startIdle() {
    if (this.reducedMotion) return;

    [...this.ringsTarget.children].forEach((ring, i) => ring.animate(
      [{ transform: "scale(1, 1)", opacity: 0.55 }, { transform: "scale(1.16, 1.75)", opacity: 0 }],
      { duration: 2600, delay: i * 866, iterations: Infinity, easing: "cubic-bezier(.2,.6,.3,1)" }
    ));
    this.breath = this.buttonTarget.animate(
      [{ transform: "scale(1)" }, { transform: "scale(1.025)" }],
      { duration: 1400, iterations: Infinity, direction: "alternate", easing: "ease-in-out" }
    );
  }

  hover() {
    if (this.phase !== "idle" || this.reducedMotion) return;

    this.sheenTarget.animate(
      [{ transform: "translateX(-140%)" }, { transform: "translateX(260%)" }],
      { duration: 800, easing: "cubic-bezier(.4,0,.2,1)" }
    );
    this.arrowTarget.style.transform = "translateX(4px)";
  }

  // The button leans toward the cursor, and its label a little further, for a touch of parallax.
  magnetize(event) {
    if (this.phase !== "idle" || this.reducedMotion) return;

    const zone = this.element.getBoundingClientRect();
    const dx = event.clientX - (zone.left + zone.width / 2);
    const dy = event.clientY - (zone.top + zone.height / 2);
    this.moverTarget.style.transition = "transform .18s ease-out";
    this.moverTarget.style.transform = `translate(${dx * 0.12}px, ${dy * 0.28}px)`;
    this.labelTarget.style.transform = `translate(${dx * 0.04}px, ${dy * 0.06}px)`;
  }

  settle() {
    this.moverTarget.style.transition = "transform .6s cubic-bezier(.2,1.4,.4,1)";
    this.moverTarget.style.transform = "";
    this.labelTarget.style.transform = "";
    this.arrowTarget.style.transform = "";
    this.release();
  }

  press() {
    if (this.phase === "idle") this.presserTarget.style.transform = "scale(.93)";
  }

  release() {
    this.presserTarget.style.transform = "";
  }

  launch(event) {
    if (this.phase !== "idle") return;
    if (event.type === "submit" && event.submitter && event.submitter !== this.buttonTarget) return;

    this.phase = "launching";
    this.heldUntil = new Promise((resolve) => this.later(resolve, this.reducedMotion ? 0 : HOLD));
    if (this.reducedMotion) return;

    const button = this.buttonTarget;
    const style = getComputedStyle(button);
    const height = button.offsetHeight;

    this.breath?.cancel();
    [...this.ringsTarget.children].forEach((ring) => ring.getAnimations().forEach((animation) => animation.cancel()));
    this.settle();

    this.labelTarget.animate(
      [{ opacity: 1, transform: "scale(1)" }, { opacity: 0, transform: "scale(.85)" }],
      { duration: 200, fill: "forwards" }
    );
    button.animate([
      { width: `${button.offsetWidth}px`, paddingLeft: style.paddingLeft, paddingRight: style.paddingRight, borderRadius: style.borderRadius, backgroundColor: style.backgroundColor },
      { width: `${height}px`, paddingLeft: "0px", paddingRight: "0px", borderRadius: `${height / 2}px`, backgroundColor: SUCCESS_COLOR }
    ], { duration: 440, delay: 60, easing: "cubic-bezier(.7,0,.2,1)", fill: "forwards" });

    this.later(() => {
      this.checkTarget.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 120, fill: "forwards" });
      this.checkTarget.querySelector("path").animate(
        [{ strokeDashoffset: 1 }, { strokeDashoffset: 0 }],
        { duration: 300, easing: "cubic-bezier(.6,0,.3,1)", fill: "forwards" }
      );
    }, 420);
    this.later(() => this.impact(), 560);
    this.later(() => this.cannons(), 820);
  }

  impact() {
    const button = this.buttonTarget;
    button.animate(
      [{ transform: "scale(1)" }, { transform: "scale(1.28)", offset: 0.3 }, { transform: "scale(.94)", offset: 0.65 }, { transform: "scale(1)" }],
      { duration: 560, easing: "ease-out" }
    );

    const rect = button.getBoundingClientRect();
    const x = rect.left + rect.width / 2;
    const y = rect.top + rect.height / 2;
    SHOCKWAVES.forEach(([color, scale, duration], i) => this.shockwave(
      x, y, { border: `3px solid ${color}` },
      [{ transform: "scale(1)", opacity: 0.7, borderWidth: "3px" }, { transform: `scale(${scale})`, opacity: 0, borderWidth: "1px" }],
      { duration, delay: i * 70, easing: "cubic-bezier(.1,.7,.3,1)", fill: "backwards" }
    ));
    // The glow grows by size rather than scale so its hole stays the button's size: the stage sits above
    // the button, and a filled disc would wash it out.
    this.shockwave(
      x, y, { background: "radial-gradient(circle, transparent 36px, #B8B3FF 44px)" },
      [{ width: "64px", height: "64px", margin: "-32px", opacity: 0.6 }, { width: "256px", height: "256px", margin: "-128px", opacity: 0 }],
      { duration: 500, easing: "ease-out" }
    );

    burst({
      particleCount: 200,
      spread: 360,
      startVelocity: 26,
      origin: { x: x / window.innerWidth, y: y / window.innerHeight }
    });
  }

  shockwave(x, y, look, keyframes, timing) {
    const ring = document.createElement("div");
    Object.assign(ring.style, {
      position: "absolute", left: `${x}px`, top: `${y}px`, width: "64px", height: "64px", margin: "-32px",
      boxSizing: "border-box", borderRadius: "50%", opacity: "0", ...look
    });
    getStage().append(ring);
    ring.animate(keyframes, timing).finished.then(() => ring.remove());
  }

  cannons() {
    burst({ particleCount: 130, angle: 54, spread: 34, startVelocity: 55, origin: { x: 0, y: 1 } });
    burst({ particleCount: 130, angle: 126, spread: 34, startVelocity: 55, origin: { x: 1, y: 1 } });
  }

  // The form's response is in: on success, hold the render it leads to; on failure, go back to idle.
  responded(event) {
    if (this.phase !== "launching") return;

    if (event.detail.fetchResponse.succeeded) {
      document.addEventListener("turbo:before-render", this.holdRender);
    } else {
      this.failed();
    }
  }

  async holdRender(event) {
    document.removeEventListener("turbo:before-render", this.holdRender);
    event.preventDefault();

    await this.heldUntil;
    const selector = this.transitionValue;
    const blocks = selector ? [...document.querySelectorAll(selector)] : [];
    // Blocks fade from wherever their opacity sits (a modal's dimmed overlay), and fixed layers fade in place
    // rather than lift, so a full-viewport backdrop doesn't shrink away from the edges.
    const exits = blocks.map((block) => {
      const lift = !this.reducedMotion && getComputedStyle(block).position !== "fixed";
      return block.animate(
        [lift ? { opacity: 0, transform: "translateY(-24px) scale(.98)" } : { opacity: 0 }],
        { duration: this.reducedMotion ? 150 : 520, easing: "cubic-bezier(.5,0,.3,1)", fill: "forwards" }
      );
    });
    await Promise.all(exits.map((animation) => animation.finished));

    if (selector) revealOnRender(selector, exits, this.reducedMotion);
    event.detail.resume();
  }

  failed() {
    if (this.phase !== "launching") return;

    document.removeEventListener("turbo:before-render", this.holdRender);
    this.timers.forEach(clearTimeout);
    this.timers = [];
    this.element.getAnimations({ subtree: true }).forEach((animation) => animation.cancel());
    this.phase = "idle";
    this.startIdle();
  }

  later(fn, ms) {
    this.timers.push(setTimeout(fn, ms));
  }
}
